/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { INetworkResourceListener } from "../../interfaces/INetworkResourceListener";
import type { INetworkResourceAvailability } from "../../types/INetworkResourceAvailability";
import { NetworkResource, NetworkResourceFailureReason } from "../../enums";
import { NetworkAvailabilityServiceImpl } from "../../network/NetworkAvailabilityServiceImpl";
import { Log } from "../../logging/Log";
import { boundedPush } from "../../utility";

/**
 * Metadata for a single inflight API call managed by the guard.
 */
interface InflightEntry {
  /** AbortController whose signal was passed to the API call. */
  controller: AbortController;

  /**
   * When retryOnReconnect is set, this closure replays the original call
   * with a fresh AbortController. Stored at registration time.
   */
  replayFn: (() => Promise<unknown>) | null;

  /**
   * When true, this call will NOT be aborted by
   * {@link NetworkGuard.cancelAllForNavigation}.
   * Used for calls that must survive navigation transitions.
   */
  bypassNavigationCancellation: boolean;
}

/**
 * Entry stored in the retry queue alongside the replay closure.
 */
interface RetryQueueEntry {
  /** The closure that replays the original API call. */
  replayFn: () => Promise<unknown>;

  /** When true, this entry is retained during navigation cancellation. */
  bypassNavigationCancellation: boolean;
}

/**
 * Network-aware singleton guard that manages inflight API calls relative
 * to internet connectivity state.
 *
 * Singleton rationale:
 * - Only one subscription to NetworkAvailabilityService (avoids duplicate
 *   listeners and event handling overhead).
 * - Unified retry queue: all interactors share a single queue so replays
 *   are batched (5-at-a-time) globally upon reconnect, preventing
 *   thundering-herd while preserving parallelism.
 * - Consistent internet-state view across all interactors (no race between
 *   per-instance state updates).
 *
 * Responsibilities:
 * 0. Dispatches internet availability hints based on API call outcomes
 *    (success/HTTP response → available; reachability error → unavailable).
 * 1. Holds each inflight API AbortController keyed by a unique call ID.
 * 2. Subscribes to internet availability change events.
 * 3. Cancels all inflight APIs and blocks new ones when internet is unavailable.
 * 4. Per-call auto-retry via TNetworkOptions.retryOnReconnect (parallel batch).
 * 5. Cancels stale inflight calls on navigation via cancelAllForNavigation().
 */
class NetworkGuard {
  private static readonly TAG: string = "NetworkGuard";

  /** Singleton instance. */
  private static mInstance: NetworkGuard | null = null;

  /** Whether internet is currently available. */
  private mIsInternetAvailable: boolean;

  /** Registry of inflight API calls keyed by unique call ID. */
  private readonly mInflightCalls: Map<string, InflightEntry>;

  /** Queue of replay entries to execute when internet returns. */
  private readonly mRetryQueue: Array<RetryQueueEntry>;

  /** Listener registered with NetworkAvailabilityService. */
  private readonly mNetworkListener: INetworkResourceListener;

  /** Auto-increment counter for generating unique call IDs. */
  private mCallIdCounter: number;

  private constructor() {
    this.mInflightCalls = new Map();
    this.mRetryQueue = [];
    this.mCallIdCounter = 0;

    // Seed initial state from the service
    const currentAvailability: INetworkResourceAvailability =
      NetworkAvailabilityServiceImpl.getInstance().getAvailability(
        NetworkResource.INTERNET
      );
    this.mIsInternetAvailable = currentAvailability.isAvailable;

    // Subscribe to internet state changes
    this.mNetworkListener = {
      onNetworkResourceAvailabilityChanged: (
        _resource: NetworkResource,
        availability: INetworkResourceAvailability
      ): void => {
        this.onInternetStateChanged(availability);
      }
    };

    NetworkAvailabilityServiceImpl.getInstance().addListener(
      NetworkResource.INTERNET,
      this.mNetworkListener
    );

    Log.d(
      NetworkGuard.TAG,
      "constructor() — subscribed to INTERNET, initial available=" +
        String(this.mIsInternetAvailable)
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Singleton accessor
  // ──────────────────────────────────────────────────────────────────

  /**
   * Returns the singleton NetworkGuard instance.
   * Creates it on first access (lazy initialization).
   */
  public static getInstance(): NetworkGuard {
    if (NetworkGuard.mInstance === null) {
      NetworkGuard.mInstance = new NetworkGuard();
    }
    return NetworkGuard.mInstance;
  }

  // ──────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────

  /**
   * Returns whether internet is currently considered available.
   */
  public isInternetAvailable(): boolean {
    return this.mIsInternetAvailable;
  }

  /**
   * Generates a unique call identifier for inflight tracking.
   * @param methodName - Logical name of the interactor method
   * @returns A unique string key
   */
  public generateCallId(methodName: string): string {
    this.mCallIdCounter += 1;
    return methodName + "_" + String(this.mCallIdCounter);
  }

  /**
   * Enqueues a replay closure into the retry queue.
   * Used when the network is down at call time but retryOnReconnect is
   * enabled — the call will automatically fire once internet returns.
   * @param replayFn - The closure that replays the original call
   * @param bypassNavigationCancellation - When true, retained on nav cancel
   */
  public enqueueForRetry(
    replayFn: () => Promise<unknown>,
    bypassNavigationCancellation: boolean = false
  ): void {
    Log.d(
      NetworkGuard.TAG,
      "enqueueForRetry() — queuing call for replay on reconnect"
    );
    boundedPush(
      this.mRetryQueue,
      { replayFn, bypassNavigationCancellation },
      NetworkGuard.RETRY_QUEUE_MAX_SIZE
    );
  }

  /**
   * Registers an inflight call with the guard.
   * @param callId - Unique identifier for the call
   * @param controller - The AbortController driving the call
   * @param replayFn - Optional replay closure for retry-on-reconnect
   * @param bypassNavigationCancellation - When true, call survives cancelAll()
   */
  public registerCall(
    callId: string,
    controller: AbortController,
    replayFn: (() => Promise<unknown>) | null,
    bypassNavigationCancellation: boolean = false
  ): void {
    this.mInflightCalls.set(callId, {
      controller,
      replayFn,
      bypassNavigationCancellation
    });
  }

  /**
   * Unregisters a completed (or failed) call from the guard.
   * @param callId - The call ID to remove
   */
  public unregisterCall(callId: string): void {
    this.mInflightCalls.delete(callId);
  }

  /**
   * Cancels all inflight API calls and removes non-bypass entries from
   * the retry queue. Intended to be invoked on navigation so stale calls
   * from the previous screen do not complete and mutate state.
   *
   * Calls and retry entries registered with
   * `bypassNavigationCancellation: true` are preserved.
   */
  public cancelAllForNavigation(): void {
    Log.i(
      NetworkGuard.TAG,
      "cancelAllForNavigation() — aborting inflight call(s), filtering retry queue"
    );

    // Collect call IDs to delete
    const callIdsToDelete: Array<string> = [];

    for (const [callId, entry] of this.mInflightCalls.entries()) {
      if (!entry.bypassNavigationCancellation) {
        entry.controller.abort();
        callIdsToDelete.push(callId);
      }
    }

    for (const callId of callIdsToDelete) {
      this.mInflightCalls.delete(callId);
    }

    // Retain only retry entries that are flagged to bypass navigation cancellation
    const retained: Array<RetryQueueEntry> = this.mRetryQueue.filter(
      (entry: RetryQueueEntry): boolean => entry.bypassNavigationCancellation
    );
    this.mRetryQueue.length = 0;
    for (const entry of retained) {
      boundedPush(this.mRetryQueue, entry, NetworkGuard.RETRY_QUEUE_MAX_SIZE);
    }
  }

  /**
   * Dispatches an internet availability hint to the NetworkAvailabilityService.
   * - When isAvailable=true: signals that reachability is intact (call succeeded
   *   or received HTTP response, even if 4xx/5xx).
   * - When isAvailable=false: signals a reachability-level error (no HTTP response).
   *
   * @param isAvailable - Whether internet is currently reachable
   * @param reason - Failure reason when isAvailable=false; ignored when true
   */
  public hintInternetAvailability(
    isAvailable: boolean,
    reason: NetworkResourceFailureReason = NetworkResourceFailureReason.INTERNET_UNREACHABLE
  ): void {
    const logLevel = isAvailable ? Log.d : Log.i;
    logLevel(
      NetworkGuard.TAG,
      "hintInternetAvailability() — dispatching hint, available=" +
        String(isAvailable) +
        (isAvailable ? "" : ", reason=" + String(reason))
    );
    NetworkAvailabilityServiceImpl.getInstance().hintNetworkResourceAvailability(
      NetworkResource.INTERNET,
      isAvailable,
      isAvailable ? null : reason
    );
  }

  /**
   * Determines whether a caught error represents a network reachability
   * failure (i.e., the request never received an HTTP response).
   *
   * Detection heuristics (axios error shape from domain-lib):
   * - error.response is null/undefined (no HTTP response received)
   * - error.code is "ERR_NETWORK" or "ECONNABORTED"
   * - error.message contains "Network Error"
   *
   * @param error - The caught error object
   * @returns true if this is a reachability-level error
   */
  public static isReachabilityError(error: unknown): boolean {
    if (error === null || error === undefined) {
      return false;
    }

    const errorRecord = error as Record<string, unknown>;

    // If there IS an HTTP response, it's not a reachability error
    const response = errorRecord.response;
    if (response !== null && response !== undefined) {
      return false;
    }

    // Check error.code for Axios network error codes
    const code = errorRecord.code;
    if (code === "ERR_NETWORK" || code === "ECONNABORTED") {
      return true;
    }

    // Check error.message for network error pattern
    const message = errorRecord.message;
    if (typeof message === "string" && message.indexOf("Network Error") >= 0) {
      return true;
    }

    return false;
  }

  // ──────────────────────────────────────────────────────────────────
  // Private — Internet state change handling
  // ──────────────────────────────────────────────────────────────────

  /**
   * Handles internet availability changes from the NetworkAvailabilityService.
   * - On unavailable: aborts all inflight calls, queues retryable ones.
   * - On available: drains the retry queue.
   */
  private onInternetStateChanged(
    availability: INetworkResourceAvailability
  ): void {
    const wasAvailable: boolean = this.mIsInternetAvailable;
    this.mIsInternetAvailable = availability.isAvailable;

    Log.i(
      NetworkGuard.TAG,
      "onInternetStateChanged() — available=" +
        String(availability.isAvailable) +
        " (was " +
        String(wasAvailable) +
        ")"
    );

    if (!availability.isAvailable && wasAvailable) {
      // Internet just went down — cancel all inflight
      this.cancelAllInflightForOffline();
    } else if (availability.isAvailable && !wasAvailable) {
      // Internet just came back — drain retry queue
      this.drainRetryQueue();
    }
  }

  /**
   * Aborts all inflight API calls and moves retryable entries to the
   * retry queue for later replay.
   */
  private cancelAllInflightForOffline(): void {
    Log.i(
      NetworkGuard.TAG,
      "cancelAllInflightForOffline() — aborting " +
        String(this.mInflightCalls.size) +
        " inflight call(s)"
    );

    this.mInflightCalls.forEach(
      (entry: InflightEntry, callId: string): void => {
        entry.controller.abort();

        if (entry.replayFn !== null) {
          Log.d(
            NetworkGuard.TAG,
            "cancelAllInflightForOffline() — queuing retry for " + callId
          );
          boundedPush(
            this.mRetryQueue,
            {
              replayFn: entry.replayFn,
              bypassNavigationCancellation: entry.bypassNavigationCancellation
            },
            NetworkGuard.RETRY_QUEUE_MAX_SIZE
          );
        }
      }
    );

    this.mInflightCalls.clear();
  }

  /** Maximum number of retries fired concurrently per batch. */
  private static readonly BATCH_SIZE: number = 5;

  /** Maximum number of entries held in the retry queue at any time. */
  private static readonly RETRY_QUEUE_MAX_SIZE: number = 20;

  /**
   * Drains the retry queue in batches of {@link BATCH_SIZE} once internet
   * is restored. Each batch fires in parallel; the next batch starts only
   * after the previous one settles. This prevents thundering-herd while
   * still achieving parallelism within each batch.
   */
  private drainRetryQueue(): void {
    if (this.mRetryQueue.length === 0) {
      return;
    }

    Log.i(
      NetworkGuard.TAG,
      "drainRetryQueue() — replaying " +
        String(this.mRetryQueue.length) +
        " queued call(s) in batches of " +
        String(NetworkGuard.BATCH_SIZE)
    );

    // Snapshot and clear so new calls queued during execution don't re-enter
    // Preserve full RetryQueueEntry to maintain bypassNavigationCancellation flag
    const queue: Array<RetryQueueEntry> = this.mRetryQueue.slice();
    this.mRetryQueue.length = 0;

    this.executeBatchedDrain(queue).catch((drainError: unknown): void => {
      Log.e(NetworkGuard.TAG, "drainRetryQueue() — drain failed", drainError);
    });
  }

  /**
   * Processes replay closures in batches of {@link BATCH_SIZE}.
   * Within each batch all calls execute in parallel. Batches are processed
   * sequentially to cap concurrent requests and prevent thundering-herd.
   * If internet drops mid-drain, remaining items are re-enqueued with their
   * original bypassNavigationCancellation flag preserved.
   */
  private async executeBatchedDrain(
    queue: Array<RetryQueueEntry>
  ): Promise<void> {
    for (let i = 0; i < queue.length; i += NetworkGuard.BATCH_SIZE) {
      // If internet went down again mid-drain, re-enqueue the rest
      if (!this.mIsInternetAvailable) {
        Log.w(
          NetworkGuard.TAG,
          "executeBatchedDrain() — internet dropped, re-enqueuing " +
            String(queue.length - i) +
            " remaining call(s)"
        );
        const remaining: Array<RetryQueueEntry> = queue.slice(i);
        for (const entry of remaining) {
          boundedPush(
            this.mRetryQueue,
            entry,
            NetworkGuard.RETRY_QUEUE_MAX_SIZE
          );
        }
        return;
      }

      const batch: Array<RetryQueueEntry> = queue.slice(
        i,
        i + NetworkGuard.BATCH_SIZE
      );

      Log.d(
        NetworkGuard.TAG,
        "executeBatchedDrain() — firing batch " +
          String(Math.floor(i / NetworkGuard.BATCH_SIZE) + 1) +
          " (" +
          String(batch.length) +
          " call(s))"
      );

      const results: Array<PromiseSettledResult<unknown>> =
        await Promise.allSettled(
          batch.map(
            (entry: RetryQueueEntry): Promise<unknown> => entry.replayFn()
          )
        );

      const failedCount: number = results.filter(
        (r: PromiseSettledResult<unknown>): boolean => r.status === "rejected"
      ).length;

      if (failedCount > 0) {
        Log.w(
          NetworkGuard.TAG,
          "executeBatchedDrain() — " +
            String(failedCount) +
            "/" +
            String(batch.length) +
            " retries failed in batch"
        );
      }
    }
  }
}

export { NetworkGuard };
